/**
 * Auto-Fix Defaults Library
 *
 * This module provides default values for all required fields in the Experience Schema
 * based on the Swift consumer requirements (DefinitionService.swift).
 *
 * Each entry maps a JSON path pattern to its required default value.
 */

export interface AutoFixRule {
    pathPattern: RegExp;
    fieldName: string;
    defaultValue: any;
    description: string;
    swiftType: string;
}

/**
 * Comprehensive auto-fix rules based on Swift DefinitionService.swift
 */
export const AUTO_FIX_RULES: AutoFixRule[] = [
    // =================================================================
    // PECaptureMetadata - Required Fields (lines 967-1052)
    // =================================================================
    {
        pathPattern: /.*\.stage\.metadata\.mode$/,
        fieldName: 'mode',
        defaultValue: 'photo',
        description: 'Capture mode (required)',
        swiftType: 'PECaptureMode (String enum)',
    },
    {
        pathPattern: /.*\.stage\.metadata\.flowId$/,
        fieldName: 'flowId',
        defaultValue: 0,
        description: 'Processing flow ID (required)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*\.stage\.metadata\.shouldNavigateAfterCapture$/,
        fieldName: 'shouldNavigateAfterCapture',
        defaultValue: false,
        description: 'Navigation flag after capture (required)',
        swiftType: 'Bool',
    },
    {
        pathPattern: /.*\.stage\.metadata\.prompt$/,
        fieldName: 'prompt',
        defaultValue: '',
        description: 'Capture prompt text (required, can be empty)',
        swiftType: 'String',
    },

    // PECaptureMetadata - Optional Fields with Defaults
    {
        pathPattern: /.*\.stage\.metadata\.countDown$/,
        fieldName: 'countDown',
        defaultValue: 3,
        description: 'Countdown seconds (optional, default: 0)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*\.stage\.metadata\.processingMessage$/,
        fieldName: 'processingMessage',
        defaultValue: 'Processing...',
        description: 'Processing message (optional)',
        swiftType: 'String?',
    },
    {
        pathPattern: /.*\.stage\.metadata\.captureMessage$/,
        fieldName: 'captureMessage',
        defaultValue: 'Smile!',
        description: 'Capture message (optional)',
        swiftType: 'String?',
    },
    {
        pathPattern: /.*\.stage\.metadata\.dynamicTextIdentifier$/,
        fieldName: 'dynamicTextIdentifier',
        defaultValue: '',
        description: 'Dynamic text identifier (optional)',
        swiftType: 'String?',
    },
    {
        pathPattern: /.*\.stage\.metadata\.captureOnMount$/,
        fieldName: 'captureOnMount',
        defaultValue: false,
        description: 'Auto-capture on mount (optional, default: false)',
        swiftType: 'Bool',
    },
    {
        pathPattern: /.*\.stage\.metadata\.skipGifGeneration$/,
        fieldName: 'skipGifGeneration',
        defaultValue: false,
        description: 'Skip GIF generation (optional, default: false)',
        swiftType: 'Bool',
    },
    {
        pathPattern: /.*\.stage\.metadata\.hasNoTimeout$/,
        fieldName: 'hasNoTimeout',
        defaultValue: false,
        description: 'No timeout flag (optional, default: false)',
        swiftType: 'Bool',
    },
    {
        pathPattern: /.*\.stage\.metadata\.takeFirstPhotoImmediately$/,
        fieldName: 'takeFirstPhotoImmediately',
        defaultValue: false,
        description: 'Take first photo immediately (optional, default: false)',
        swiftType: 'Bool',
    },
    {
        pathPattern: /.*\.stage\.metadata\.includeIndividualPhotos$/,
        fieldName: 'includeIndividualPhotos',
        defaultValue: true,
        description: 'Include individual photos (optional, default: true)',
        swiftType: 'Bool',
    },
    {
        pathPattern: /.*\.stage\.metadata\.navigateAfterCaptureDelay$/,
        fieldName: 'navigateAfterCaptureDelay',
        defaultValue: 0,
        description: 'Delay before navigation (optional, default: 0)',
        swiftType: 'Int',
    },

    // =================================================================
    // PEProcessingMetadata - Required Fields (lines 1054-1113)
    // =================================================================
    {
        pathPattern: /.*\.stage\.metadata\.type$/,
        fieldName: 'type',
        defaultValue: 'composite',
        description: 'Processing type (required)',
        swiftType: 'PEProcessingType (String enum)',
    },
    {
        pathPattern: /.*processing.*\.flowId$/,
        fieldName: 'flowId',
        defaultValue: 0,
        description: 'Processing flow ID (required)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*\.stage\.metadata\.inputs$/,
        fieldName: 'inputs',
        defaultValue: [],
        description: 'Input screen IDs (required)',
        swiftType: '[String]',
    },
    {
        pathPattern: /.*\.stage\.metadata\.shouldNavigateAfterProcessing$/,
        fieldName: 'shouldNavigateAfterProcessing',
        defaultValue: false,
        description: 'Navigation flag after processing (required)',
        swiftType: 'Bool',
    },
    {
        pathPattern: /.*\.stage\.metadata\.height$/,
        fieldName: 'height',
        defaultValue: 1800,
        description: 'Processing output height (required)',
        swiftType: 'CGFloat',
    },
    {
        pathPattern: /.*\.stage\.metadata\.width$/,
        fieldName: 'width',
        defaultValue: 1200,
        description: 'Processing output width (required)',
        swiftType: 'CGFloat',
    },
    {
        pathPattern: /.*\.stage\.metadata\.expectedPhotoCount$/,
        fieldName: 'expectedPhotoCount',
        defaultValue: 1,
        description: 'Expected photo count (required)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*\.stage\.metadata\.processingPhotoInstructions$/,
        fieldName: 'processingPhotoInstructions',
        defaultValue: [],
        description: 'Processing instructions array (required)',
        swiftType: '[CompositePhotoProcessingInstruction]',
    },
    {
        pathPattern: /.*\.stage\.metadata\.autoPrint$/,
        fieldName: 'autoPrint',
        defaultValue: false,
        description: 'Auto-print flag (optional, default: false)',
        swiftType: 'Bool',
    },

    // =================================================================
    // PEUIQRMetadata - Required Fields (lines 406-451)
    // For: currentSessionQR, selectedQR, paymentQR, galleryQR, actionQr
    // =================================================================
    {
        pathPattern: /.*elements\[\d+\]\.metadata\.width$/,
        fieldName: 'width',
        defaultValue: 200,
        description: 'Element width (required for QR, image elements)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*elements\[\d+\]\.metadata\.height$/,
        fieldName: 'height',
        defaultValue: 200,
        description: 'Element height (required for QR, image elements)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*qr.*\.metadata\.foregroundColor$/i,
        fieldName: 'foregroundColor',
        defaultValue: '#000000',
        description: 'QR foreground color (optional, default: #000000)',
        swiftType: 'String',
    },
    {
        pathPattern: /.*qr.*\.metadata\.backgroundColor$/i,
        fieldName: 'backgroundColor',
        defaultValue: '#FFFFFF',
        description: 'QR background color (optional, default: #FFFFFF)',
        swiftType: 'String',
    },

    // =================================================================
    // PEUIPaymentQRMetadata - Required Fields (lines 356-404)
    // =================================================================
    {
        pathPattern: /.*paymentQR.*\.metadata\.destination$/,
        fieldName: 'destination',
        defaultValue: '',
        description: 'Payment success destination screen (required)',
        swiftType: 'String',
    },

    // =================================================================
    // PEUITextMetadata - Required Fields (lines 453-502)
    // =================================================================
    {
        pathPattern: /.*\.(text|dynamicText).*\.metadata\.text$/,
        fieldName: 'text',
        defaultValue: '',
        description: 'Text content (required)',
        swiftType: 'String',
    },
    {
        pathPattern:
            /.*\.(text|dynamicText|button|input).*\.metadata\.fontSize$/,
        fieldName: 'fontSize',
        defaultValue: 16,
        description: 'Font size (required)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*\.metadata\.fontFace$/,
        fieldName: 'fontFace',
        defaultValue: 'system',
        description: 'Font face (optional, default: system)',
        swiftType: 'String',
    },
    {
        pathPattern: /.*\.metadata\.fontWeight$/,
        fieldName: 'fontWeight',
        defaultValue: 400,
        description: 'Font weight (optional, default: 400)',
        swiftType: 'Int?',
    },
    {
        pathPattern: /.*\.metadata\.horizontalMargin$/,
        fieldName: 'horizontalMargin',
        defaultValue: 0,
        description: 'Horizontal margin (optional, default: 0)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*\.metadata\.verticalMargin$/,
        fieldName: 'verticalMargin',
        defaultValue: 0,
        description: 'Vertical margin (optional, default: 0)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*\.metadata\.verticalOffset$/,
        fieldName: 'verticalOffset',
        defaultValue: 0,
        description: 'Vertical offset (optional, default: 0)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*\.metadata\.horizontalOffset$/,
        fieldName: 'horizontalOffset',
        defaultValue: 0,
        description: 'Horizontal offset (optional, default: 0)',
        swiftType: 'Int',
    },

    // =================================================================
    // PEUIDynamicTextMetadata - Required Fields (lines 592-656)
    // =================================================================
    {
        pathPattern: /.*dynamicText.*\.metadata\.identifier$/,
        fieldName: 'identifier',
        defaultValue: 'dynamic-text',
        description: 'Dynamic text identifier (required)',
        swiftType: 'String',
    },
    {
        pathPattern: /.*dynamicText.*\.metadata\.action$/,
        fieldName: 'action',
        defaultValue: [],
        description: 'Action array (optional, default: [])',
        swiftType: '[String]',
    },

    // =================================================================
    // PEUIButtonMetadata - Required Fields (lines 707-830)
    // =================================================================
    {
        pathPattern: /.*button.*\.metadata\.text$/,
        fieldName: 'text',
        defaultValue: 'Button',
        description: 'Button text (required)',
        swiftType: 'String',
    },
    {
        pathPattern: /.*button.*\.metadata\.action$/,
        fieldName: 'action',
        defaultValue: ['navigate'],
        description: 'Button action (required)',
        swiftType: '[String]',
    },
    {
        pathPattern: /.*button.*\.metadata\.horizontalPadding$/,
        fieldName: 'horizontalPadding',
        defaultValue: 0,
        description: 'Horizontal padding (optional, default: 0)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*button.*\.metadata\.verticalPadding$/,
        fieldName: 'verticalPadding',
        defaultValue: 0,
        description: 'Vertical padding (optional, default: 0)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*button.*\.metadata\.cornerRadius$/,
        fieldName: 'cornerRadius',
        defaultValue: 0,
        description: 'Corner radius (optional, default: 0)',
        swiftType: 'Int',
    },

    // =================================================================
    // PEUIInputMetadata - Required Fields (lines 851-917)
    // =================================================================
    {
        pathPattern: /.*input.*\.metadata\.key$/,
        fieldName: 'key',
        defaultValue: 'inputValue',
        description: 'Input key for value storage (required)',
        swiftType: 'String',
    },
    {
        pathPattern: /.*input.*\.metadata\.inputType$/,
        fieldName: 'inputType',
        defaultValue: 'text',
        description: 'Input type (required)',
        swiftType: 'PEUIInputType (String enum)',
    },
    {
        pathPattern: /.*input.*\.metadata\.width$/,
        fieldName: 'width',
        defaultValue: 300,
        description: 'Input width (optional, default: 300)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*input.*\.metadata\.cornerRadius$/,
        fieldName: 'cornerRadius',
        defaultValue: 0,
        description: 'Input corner radius (optional, default: 0)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*input.*\.metadata\.isFocused$/,
        fieldName: 'isFocused',
        defaultValue: false,
        description: 'Input focus state (optional, default: false)',
        swiftType: 'Bool',
    },
    {
        pathPattern: /.*input.*\.metadata\.style$/,
        fieldName: 'style',
        defaultValue: 'roundedBox',
        description: 'Input style (optional, default: roundedBox)',
        swiftType: 'PEUIInputStyle (String enum)',
    },
    {
        pathPattern: /.*input.*\.metadata\.placeholder$/,
        fieldName: 'placeholder',
        defaultValue: '',
        description: 'Input placeholder (optional, default: empty)',
        swiftType: 'String',
    },

    // =================================================================
    // PEUIImageMetadata - Required Fields (lines 275-314)
    // =================================================================
    {
        pathPattern: /.*image.*\.metadata\.width$/,
        fieldName: 'width',
        defaultValue: 100,
        description: 'Image width (optional, default: 0)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*image.*\.metadata\.height$/,
        fieldName: 'height',
        defaultValue: 100,
        description: 'Image height (optional, default: 0)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*image.*\.metadata\.useImageDimensions$/,
        fieldName: 'useImageDimensions',
        defaultValue: true,
        description: 'Use native image dimensions (optional, default: true)',
        swiftType: 'Bool',
    },
    {
        pathPattern: /.*image.*\.metadata\.cornerRadius$/,
        fieldName: 'cornerRadius',
        defaultValue: 0,
        description: 'Image corner radius (optional, default: 0)',
        swiftType: 'Int',
    },

    // =================================================================
    // PEUIOutputPreviewMetadata - Required Fields (lines 316-354)
    // =================================================================
    {
        pathPattern: /.*outputPreview.*\.metadata\.width$/,
        fieldName: 'width',
        defaultValue: 200,
        description: 'Output preview width (required)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*outputPreview.*\.metadata\.height$/,
        fieldName: 'height',
        defaultValue: 250,
        description: 'Output preview height (required)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*outputPreview.*\.metadata\.rotation$/,
        fieldName: 'rotation',
        defaultValue: 0,
        description: 'Output rotation (optional, default: 0)',
        swiftType: 'Double',
    },

    // =================================================================
    // PEUIGridMetadata - Required/Optional Fields (lines 504-590)
    // For: galleryGrid, mediaGrid, sessionGrid
    // =================================================================
    {
        pathPattern: /.*Grid.*\.metadata\.columns$/,
        fieldName: 'columns',
        defaultValue: 3,
        description: 'Grid columns (optional, default: 3)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*Grid.*\.metadata\.spacing$/,
        fieldName: 'spacing',
        defaultValue: 5,
        description: 'Grid spacing (optional, default: 5)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*Grid.*\.metadata\.width$/,
        fieldName: 'width',
        defaultValue: 600,
        description: 'Grid width (optional, default: 600)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*Grid.*\.metadata\.height$/,
        fieldName: 'height',
        defaultValue: 900,
        description: 'Grid height (optional, default: 900)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*Grid.*\.metadata\.selectionColor$/,
        fieldName: 'selectionColor',
        defaultValue: '#FFFFFF',
        description: 'Grid selection color (optional, default: #FFFFFF)',
        swiftType: 'String',
    },
    {
        pathPattern: /.*Grid.*\.metadata\.cornerRadius$/,
        fieldName: 'cornerRadius',
        defaultValue: 0,
        description: 'Grid item corner radius (optional, default: 0)',
        swiftType: 'Int',
    },

    // =================================================================
    // PEUIElementLiveViewMetadata - Required Fields (lines 166-204)
    // =================================================================
    {
        pathPattern: /.*liveView.*\.metadata\.width$/,
        fieldName: 'width',
        defaultValue: 300,
        description: 'Live view width (required)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*liveView.*\.metadata\.height$/,
        fieldName: 'height',
        defaultValue: 400,
        description: 'Live view height (required)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*liveView.*\.metadata\.showControls$/,
        fieldName: 'showControls',
        defaultValue: false,
        description: 'Show live view controls (optional, default: false)',
        swiftType: 'Bool',
    },

    // =================================================================
    // PEUIElementGroupMetadata - Required Fields (lines 103-145)
    // =================================================================
    {
        pathPattern: /.*group.*\.metadata\.name$/,
        fieldName: 'name',
        defaultValue: 'group',
        description: 'Group name (required)',
        swiftType: 'String',
    },
    {
        pathPattern: /.*group.*\.metadata\.direction$/,
        fieldName: 'direction',
        defaultValue: 'column',
        description: 'Group layout direction (optional, default: column)',
        swiftType: 'LayoutDirection (String enum)',
    },
    {
        pathPattern: /.*group.*\.metadata\.spacing$/,
        fieldName: 'spacing',
        defaultValue: 8,
        description: 'Group element spacing (optional, default: 8)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*group.*\.metadata\.alignment$/,
        fieldName: 'alignment',
        defaultValue: 'center',
        description: 'Group alignment (optional, default: center)',
        swiftType: 'Alignment (String enum)',
    },

    // =================================================================
    // PEUIElementArtworkSelectorMetadata - Required Fields (lines 48-101)
    // =================================================================
    {
        pathPattern: /.*artworkSelector.*\.metadata\.source$/,
        fieldName: 'source',
        defaultValue: '',
        description: 'Artwork source screen ID (required)',
        swiftType: 'String?',
    },
    {
        pathPattern: /.*artworkSelector.*\.metadata\.flowIds$/,
        fieldName: 'flowIds',
        defaultValue: [],
        description: 'Flow IDs for artwork options (required)',
        swiftType: '[Int]',
    },
    {
        pathPattern: /.*artworkSelector.*\.metadata\.optionShape$/,
        fieldName: 'optionShape',
        defaultValue: 'circle',
        description: 'Option shape (optional, default: circle)',
        swiftType: 'OptionShape (String enum)',
    },
    {
        pathPattern: /.*artworkSelector.*\.metadata\.optionBorderWidth$/,
        fieldName: 'optionBorderWidth',
        defaultValue: 0,
        description: 'Option border width (optional, default: 0)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*artworkSelector.*\.metadata\.optionBorderColor$/,
        fieldName: 'optionBorderColor',
        defaultValue: '#ffffff',
        description: 'Option border color (optional, default: #ffffff)',
        swiftType: 'String',
    },
    {
        pathPattern: /.*artworkSelector.*\.metadata\.optionWidth$/,
        fieldName: 'optionWidth',
        defaultValue: 100,
        description: 'Option width (optional, default: 100)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*artworkSelector.*\.metadata\.optionHeight$/,
        fieldName: 'optionHeight',
        defaultValue: 100,
        description: 'Option height (optional, default: 100)',
        swiftType: 'Int',
    },

    // =================================================================
    // PEDisplayProperties - Required Fields (lines 1115-1123)
    // =================================================================
    {
        pathPattern: /.*displayProperties\.backgroundColor$/,
        fieldName: 'backgroundColor',
        defaultValue: '#FFFFFF',
        description: 'Background color (required)',
        swiftType: 'String',
    },
    {
        pathPattern: /.*displayProperties\.backgroundMediaUrl$/,
        fieldName: 'backgroundMediaUrl',
        defaultValue: '',
        description: 'Background media URL (required)',
        swiftType: 'String',
    },
    {
        pathPattern: /.*displayProperties\.foregroundColor$/,
        fieldName: 'foregroundColor',
        defaultValue: '#000000',
        description: 'Foreground color (required)',
        swiftType: 'String',
    },
    {
        pathPattern: /.*displayProperties\.buttonColor$/,
        fieldName: 'buttonColor',
        defaultValue: '#007AFF',
        description: 'Button color (required)',
        swiftType: 'String',
    },
    {
        pathPattern: /.*displayProperties\.buttonTextColor$/,
        fieldName: 'buttonTextColor',
        defaultValue: '#FFFFFF',
        description: 'Button text color (required)',
        swiftType: 'String',
    },

    // =================================================================
    // PEQueryMetadata - Required Fields (lines 942-965)
    // =================================================================
    {
        pathPattern: /.*query.*\.metadata\.descriptors$/,
        fieldName: 'descriptors',
        defaultValue: [],
        description: 'Query descriptors array (required)',
        swiftType: '[PEQueryDescriptor]',
    },
    {
        pathPattern: /.*query.*\.metadata\.destination$/,
        fieldName: 'destination',
        defaultValue: '',
        description: 'Query destination screen (required)',
        swiftType: 'String',
    },

    // =================================================================
    // UIElement - Required Fields (lines 1246-1276)
    // =================================================================
    {
        pathPattern: /.*elements\[\d+\]\.id$/,
        fieldName: 'id',
        defaultValue: 'element-' + Math.random().toString(36).substr(2, 6),
        description: 'Element ID (required)',
        swiftType: 'String',
    },
    {
        pathPattern: /.*elements\[\d+\]\.order$/,
        fieldName: 'order',
        defaultValue: 0,
        description: 'Element order (required)',
        swiftType: 'Int',
    },
    {
        pathPattern: /.*elements\[\d+\]\.type$/,
        fieldName: 'type',
        defaultValue: 'text',
        description: 'Element type (required)',
        swiftType: 'PEUIElementTypeName (String enum)',
    },
    {
        pathPattern: /.*elements\[\d+\]\.layer$/,
        fieldName: 'layer',
        defaultValue: 0,
        description: 'Element layer (optional, default: 0)',
        swiftType: 'Int',
    },

    // =================================================================
    // Screen - Required Fields (lines 1310-1367)
    // =================================================================
    {
        pathPattern: /.*screens\[\d+\]\.id$/,
        fieldName: 'id',
        defaultValue: 'screen-' + Math.random().toString(36).substr(2, 6),
        description: 'Screen ID (required)',
        swiftType: 'String',
    },
    {
        pathPattern: /.*screens\[\d+\]\.elements$/,
        fieldName: 'elements',
        defaultValue: [],
        description: 'Elements array (required)',
        swiftType: '[PEUIElementWrapper]',
    },
];

/**
 * Infer the type value from the parent element
 * For example: screens[0].elements[2].metadata.type should match screens[0].elements[2].type
 */
function inferTypeFromParentElement(
    path: string,
    definition: any,
): string | undefined {
    try {
        // Parse path to find parent element
        // Example: "screens[0].elements[2].metadata.type" -> need screens[0].elements[2].type
        const metadataIndex = path.lastIndexOf('.metadata.type');
        if (metadataIndex === -1) return undefined;

        const parentPath = path.substring(0, metadataIndex);

        // Navigate to parent element in definition
        const pathParts = parentPath.split(/[.[\]]/).filter((p) => p);
        let current = definition;

        for (const part of pathParts) {
            if (!current) return undefined;

            if (!isNaN(Number(part))) {
                current = current[Number(part)];
            } else {
                current = current[part];
            }
        }

        // Get the type from parent element
        if (current && typeof current.type === 'string') {
            return current.type;
        }

        return undefined;
    } catch (error) {
        console.error('Error inferring type from parent:', error);
        return undefined;
    }
}

/**
 * Find the appropriate auto-fix rule for a given path
 */
export function findAutoFixRule(path: string): AutoFixRule | undefined {
    return AUTO_FIX_RULES.find((rule) => rule.pathPattern.test(path));
}

/**
 * Get auto-fix value for a given path and error message
 * @param path - JSON path to the field with error
 * @param message - Error message
 * @param definition - Full definition object (optional, for context-aware fixes)
 */
export function getAutoFixValue(
    path: string,
    message: string,
    definition?: any,
): any {
    const lowerMessage = message.toLowerCase();

    // Special case: videoProcessingFlowId should be removed if empty string
    if (
        path.includes('videoProcessingFlowId') &&
        lowerMessage.includes('empty string')
    ) {
        return null; // Signal to remove the field
    }

    // Special case: metadata.type should match parent element's type
    if (path.endsWith('.metadata.type') && definition) {
        const parentType = inferTypeFromParentElement(path, definition);
        if (parentType) {
            return parentType;
        }
    }

    // Find matching rule
    const rule = findAutoFixRule(path);
    if (rule) {
        return rule.defaultValue;
    }

    // Fallback logic for unmatched patterns
    const lowerPath = path.toLowerCase();

    // Booleans - default to false
    if (
        lowerMessage.includes('must be a boolean') ||
        lowerMessage.includes('boolean')
    ) {
        return false;
    }

    // Numbers - try to infer from context
    if (
        lowerMessage.includes('must be a number') ||
        lowerMessage.includes('number')
    ) {
        if (lowerPath.includes('count')) return 0;
        if (lowerPath.includes('width') || lowerPath.includes('height'))
            return 200;
        if (lowerPath.includes('padding') || lowerPath.includes('margin'))
            return 0;
        if (lowerPath.includes('offset')) return 0;
        if (lowerPath.includes('radius')) return 0;
        if (lowerPath.includes('size')) return 16;
        if (lowerPath.includes('weight')) return 400;
        return 0;
    }

    // Strings - default to empty
    if (
        lowerMessage.includes('must be a string') ||
        lowerMessage.includes('string')
    ) {
        return '';
    }

    // Arrays - default to empty array
    if (
        lowerMessage.includes('must be an array') ||
        lowerMessage.includes('array')
    ) {
        return [];
    }

    return undefined;
}

/**
 * Get description for an auto-fix
 */
export function getAutoFixDescription(path: string): string {
    const rule = findAutoFixRule(path);
    return rule ? rule.description : 'Set to default value';
}

/**
 * Check if a path can be auto-fixed
 * @param path - JSON path to the field with error
 * @param message - Error message
 * @param definition - Full definition object (optional, for context-aware fixes)
 */
export function canAutoFix(
    path: string,
    message: string,
    definition?: any,
): boolean {
    return getAutoFixValue(path, message, definition) !== undefined;
}
